import type { StaffSessionDto } from '@planuze/admin-contracts';
import { Avatar, Button } from '@planuze/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { useCommonContent } from '../content/sections/common.js';
import { clearStaffSessionCache } from '../hooks/use-staff-session.js';
import { logout } from '../lib/api-client.js';
import { useStaffSessionStore } from '../stores/staff-session-store.js';

interface UserMenuProps {
  readonly staff: StaffSessionDto;
}

/* Identidade do operador logado + logout (ADR-0700). Avatar com iniciais + email/role; logout
   encerra a sessão no servidor, limpa a metadata local + o cache da sessão e volta ao login. */
export function UserMenu({ staff }: UserMenuProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clear = useStaffSessionStore((state) => state.clear);
  const copy = useCommonContent().userMenu;

  const handleLogout = async (): Promise<void> => {
    await logout();
    clear();
    clearStaffSessionCache(queryClient);
    await navigate({ to: '/login' });
  };

  return (
    <div className="flex items-center gap-2.5">
      <Avatar name={staff.email} size="sm" />
      <div className="hidden text-right leading-tight sm:block">
        <div className="max-w-[16rem] truncate text-sm font-medium text-foreground">
          {staff.email}
        </div>
        <div className="text-xs text-muted-foreground">{copy.roleLabel(staff.role)}</div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        aria-label={copy.signOutAriaLabel}
        onClick={() => {
          handleLogout().catch(() => undefined);
        }}
      >
        <LogOut className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
